/* eslint-disable max-lines */
import React, { useEffect } from 'react';
import { getSelectedCommunicationWithEvents } from 'selectors/communications';
import { useSelector } from 'store';

import { setMatchNavigation } from 'actions';
import { NEW_CHEVRON_LEFT } from 'constants/filterIcons';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getCampaignsRulesFromCommunication,
  getMatchNavigation,
  getSelectedCampaign,
  getSelectedRule,
  getSelectedSection,
} from 'selectors/envelopeReview';
import { getRuleLines } from 'selectors/events';

const EnvelopeMatchNavigation: React.FC = () => {
  const dispatch = useDispatch();

  const { envelopeId } = useParams<{ envelopeId: string }>();

  const selectedCommunication = useSelector(getSelectedCommunicationWithEvents);
  const selectedRule = useSelector(getSelectedRule);
  const selectedCampaign = useSelector(getSelectedCampaign);
  const selectedSection = useSelector(getSelectedSection);
  const { index: navigationIndex } = useSelector(getMatchNavigation);

  const campaignsRules = useSelector((state) =>
    getCampaignsRulesFromCommunication(state, selectedCommunication?.uuid || '')
  );

  const ruleLines = useSelector((state) =>
    getRuleLines(
      state,
      selectedCommunication?.uuid || '',
      (selectedSection === 'history').toString(),
      (selectedSection === 'history').toString()
    )
  );

  let totalNavigationItems = 0;

  const rules = campaignsRules?.[selectedCampaign || 0]?.rules || [];
  const campaignRulesNavigation = Object.entries(ruleLines)
    .reduce<
      {
        line: number;
        referenceNodes: number[];
      }[]
    >((acc, [curKey, curValue]) => {
      if (rules.find((r) => r.rule_group_uuid === curKey)) {
        return [...acc, ...curValue];
      }
      return acc;
    }, [])
    .sort((a, b) => {
      if (a.line > b.line) return 1;
      if (a.line < b.line) return -1;
      if (a.referenceNodes[0] > b.referenceNodes[0]) return 1;
      return -1;
    });

  const ruleNavigation = (ruleLines[selectedRule?.rule_group_uuid || ''] || []).sort((a, b) => {
    if (a.line > b.line) return 1;
    if (a.line < b.line) return -1;
    if (a.referenceNodes[0] > b.referenceNodes[0]) return 1;
    return -1;
  });

  if (selectedRule) {
    totalNavigationItems = ruleNavigation.length || 0;
  } else if (campaignsRules && selectedCampaign && campaignsRules[selectedCampaign]) {
    totalNavigationItems = Object.entries(ruleLines).reduce((acc, [curKey, curValue]) => {
      if (rules.find((r) => r.rule_group_uuid === curKey)) {
        return acc + curValue.length;
      }
      return acc;
    }, 0);
  }

  // selecting different campaign/rule
  useEffect(() => {
    if (selectedRule) {
      dispatch(
        setMatchNavigation({
          index: 0,
          line: ruleNavigation[0]?.line || 0,
          referenceNodes: ruleNavigation[0]?.referenceNodes || [],
        })
      );
    } else {
      dispatch(
        setMatchNavigation({
          index: 0,
          line: campaignRulesNavigation[0]?.line || 0,
          referenceNodes: campaignRulesNavigation[0]?.referenceNodes || [],
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelopeId, selectedRule, selectedCampaign, dispatch]);

  // scroll control
  useEffect(() => {
    if (selectedRule && selectedRule.rule_group_uuid) {
      const element = document
        .querySelector(
          `div[data-rule-id="${selectedRule.rule_group_uuid}"][data-index="${ruleNavigation[navigationIndex]?.line}"]`
        )
        ?.querySelector(`#supernode-${ruleNavigation[navigationIndex].referenceNodes[0]}`);

      if (element && element.scrollIntoView) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      if (navigationIndex === 0) {
        const element = document.getElementById('envelope-content');
        if (element && element.scrollIntoView) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      if (selectedCampaign) {
        const element = document
          .querySelector(
            `div[data-campaign-id="${selectedCampaign}"][data-index="${
              campaignRulesNavigation[navigationIndex - 1].line
            }"]`
          )
          ?.querySelector(
            `#supernode-${campaignRulesNavigation[navigationIndex - 1].referenceNodes[0]}`
          );

        if (element && element.scrollIntoView) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRule, selectedCampaign, navigationIndex]);

  const handlePreviousMatch = (): void => {
    if (selectedRule) {
      dispatch(
        setMatchNavigation({
          index: navigationIndex - 1,
          line: ruleNavigation[navigationIndex - 1].line,
          referenceNodes: ruleNavigation[navigationIndex - 1].referenceNodes,
        })
      );
    } else {
      dispatch(
        setMatchNavigation({
          index: navigationIndex - 1,
          line: campaignRulesNavigation[navigationIndex - 1].line,
          referenceNodes: campaignRulesNavigation[navigationIndex - 1].referenceNodes,
        })
      );
    }
  };

  const handleNextMatch = (): void => {
    if (selectedRule) {
      dispatch(
        setMatchNavigation({
          index: navigationIndex + 1,
          line: ruleNavigation[navigationIndex + 1].line,
          referenceNodes: ruleNavigation[navigationIndex + 1].referenceNodes,
        })
      );
    } else {
      dispatch(
        setMatchNavigation({
          index: navigationIndex + 1,
          line: campaignRulesNavigation[navigationIndex].line,
          referenceNodes: campaignRulesNavigation[navigationIndex].referenceNodes,
        })
      );
    }
  };

  let currentIndex = selectedRule ? navigationIndex + 1 : navigationIndex;
  currentIndex = currentIndex <= totalNavigationItems ? currentIndex : totalNavigationItems;

  return (
    <div className="flex flex-row gap-2 items-center self-end">
      <span className="whitespace-no-wrap">
        {currentIndex} of {totalNavigationItems} matches
      </span>
      <button
        type="button"
        className={`flex justify-center w-4 focus:outline-none ${
          (selectedRule && currentIndex === 1) || currentIndex === 0
            ? 'opacity-50 cursor-default'
            : ''
        }`}
        onClick={handlePreviousMatch}
        disabled={currentIndex === 0}
      >
        {NEW_CHEVRON_LEFT}
      </button>
      <button
        type="button"
        className={`flex justify-center w-4 transform rotate-180 focus:outline-none ${
          currentIndex === totalNavigationItems ? 'opacity-50 cursor-default' : ''
        }`}
        onClick={handleNextMatch}
        disabled={currentIndex === totalNavigationItems}
      >
        {NEW_CHEVRON_LEFT}
      </button>
    </div>
  );
};
export default EnvelopeMatchNavigation;
